import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { SliderWithTitle } from '@hivebrite/design-system/Slider';
import { useBreakpoint } from '@hivebrite/design-system/Theme';

import { EVENT_COVER_HEIGHT } from 'hb-builder/components/BuilderComponents/dynamic/Slider/constants';
import type { SliderLayoutsProps } from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts';
import { SLIDER_TYPES } from 'hb-builder/components/BuilderComponents/dynamic/Slider/SliderLayouts/types';
import { perPageBreakpoints } from 'hb-builder/components/BuilderComponents/dynamic/Slider/utils/breakpoints';
import { getHeadline } from 'hb-builder/components/BuilderComponents/dynamic/Slider/utils/getHeadline';
import type { Event } from 'hb-builder/components/BuilderComponents/dynamic/types/event';
import { getArrowTopPositionInSlider } from 'hb-builder/utils/getArrowTopPositionInSlider';
import { goToModuleDetailPage } from 'hb-builder/utils/goToModuleDetailPage';
import EventCardLoading from 'hb-react/shared/apps/Events/components/EventsIndexContainer/components/EventCardLoading';
import { buildItems } from 'hb-react/shared/apps/Events/components/EventsIndexContainer/utils';
import { APP_TYPE } from 'hb-react/types/appType';

import ItemComponent from './ItemComponent';

type EventsProps = {
  data: Event[];
  isLoading: boolean;
} & Omit<SliderLayoutsProps, 'dynamicAttributes' | 'isPublic' | 'type'>;

const Events = ({
  app,
  data: events,
  ids,
  isLoading,
  item,
  navigate,
  whiteLabelUrl,
}: EventsProps) => {
  const numberOfItems =
    item?.attributes?.dynamicAttributes.query?.numberOfItems;
  const breakpoint = useBreakpoint();

  const title = getHeadline(item, SLIDER_TYPES.EVENTS);

  const isBackOffice = app === APP_TYPE.BACKOFFICE;

  const navTopPosition = getArrowTopPositionInSlider(EVENT_COVER_HEIGHT);

  const url = whiteLabelUrl
    ? `https://${whiteLabelUrl}/events`
    : ids?.topic
      ? Routes.topic_events_index_path(ids.topic)
      : Routes.events_path();

  const linkText =
    breakpoint !== 's' ? s__('Builder|See all events') : s__('Builder|See all');

  const sliderLink = isBackOffice
    ? { text: linkText, url: '#', target: '' }
    : { text: linkText, url, target: '_self' };

  if (!events) {
    return null;
  }

  const renderLoadingComponent = () => (
    <StyledBox>
      <EventCardLoading />
    </StyledBox>
  );

  return (
    <Box py={4}>
      <SliderWithTitle
        key={`events-slider-with-${numberOfItems}-items`}
        link={sliderLink}
        perPageBreakpoints={perPageBreakpoints[SLIDER_TYPES.EVENTS]}
        navTopPosition={navTopPosition}
        title={title}
        items={buildItems({
          events,
          onEventCardClick: (event) =>
            ids &&
            navigate &&
            goToModuleDetailPage({
              event,
              ids,
              isBackOffice,
              navigate,
            }),
        })}
        ItemComponent={ItemComponent}
        total={events.length}
        loading={isLoading}
        LoadingItemComponent={renderLoadingComponent}
      />
    </Box>
  );
};

export default Events;

const StyledBox = styled(Box).attrs(() => ({
  display: 'block',
  height: '100%',
  py: 1,
}))``;
